'use strict';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
} from '@mui/material';
import { useSearchProjectsQuery } from '@/redux/services/portal/admin.api';
import { SearchBox } from '@/components/index.jsx';
import './AddScopeModal.scss';

const AddProjectToUserModal = ({
	open,
	onCancel,
	onConfirm,
	pageSize,
	user,
	getProjectId,
	getProjectCode,
	getProjectTitle,
	existingProjectIds,
	isSubmitting,
}) => {
	const [searchText, setSearchText] = useState('');
	const [selectedProjects, setSelectedProjects] = useState(new Map());

	useEffect(() => {
		if (open) {
			setSearchText('');
			setSelectedProjects(new Map());
		}
	}, [open]);

	const shouldSearch = open && searchText.trim().length >= 1;
	const searchQuery = shouldSearch ? searchText.toUpperCase() : '';
	const searchResult = useSearchProjectsQuery(
		{ searchText: searchQuery, skip: 0, take: pageSize },
		{ skip: !shouldSearch }
	);

	const results = useMemo(() => {
		const items = searchResult.data || [];
		const mapped = items.map((item) => ({
			...item,
			projectId: getProjectId(item),
			projectCode: getProjectCode(item),
			projectTitle: getProjectTitle(item),
		}));
		const excludedIds = new Set([
			...existingProjectIds,
			...Array.from(selectedProjects.keys()),
		]);
		return mapped.filter((item) => {
			const projectId = getProjectId(item);
			return projectId && !excludedIds.has(projectId);
		});
	}, [searchResult.data, getProjectId, getProjectCode, getProjectTitle, existingProjectIds, selectedProjects]);
	const selectedItems = useMemo(() => Array.from(selectedProjects.values()), [selectedProjects]);

	const handleToggleProject = (project) => {
		const projectId = getProjectId(project);
		if (!projectId) return;
		setSelectedProjects((prev) => {
			const next = new Map(prev);
			if (next.has(projectId)) {
				next.delete(projectId);
			} else {
				next.set(projectId, project);
			}
			return next;
		});
	};

	const handleConfirm = () => {
		if (selectedProjects.size === 0 || isSubmitting) return;
		onConfirm(Array.from(selectedProjects.values()));
	};

	return (
		<Box className="AddScopeModal" sx={{ mt: 1 }}>
			<SearchBox
				iconColor="tertiary"
				showResults={false}
				hintText="Search projects"
				autoComplete="off"
				name="add-project-search"
				disabled={!user || isSubmitting}
				onChange={(value) => setSearchText(value)}
				handleCancel={() => setSearchText('')}
				mapData={{ primaryText: 'name', value: 'id' }}
				dataSource={[]}
			/>
			{!user ? (
				<Typography variant="body2" sx={{ mt: 2 }}>
					Select a user before adding a project.
				</Typography>
			) : null}
			{user && !shouldSearch ? (
				<Typography variant="body2" sx={{ mt: 2 }}>
					Start typing to search for a project.
				</Typography>
			) : null}
			{searchResult.isFetching ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
					<CircularProgress size={20} />
				</Box>
			) : null}
			{selectedItems.length > 0 ? (
				<>
					<Typography variant="subtitle2" sx={{ mt: 2 }}>
						Selected Projects
					</Typography>
					<List dense className="AddScopeModalList">
						{selectedItems.map((project) => (
							<ListItem
								button
								key={getProjectId(project)}
								selected
							onClick={() => {
								if (!isSubmitting) {
									handleToggleProject(project);
								}
							}}
							>
								<ListItemIcon>
									<Checkbox
										edge="start"
										checked
										tabIndex={-1}
										disableRipple
									disabled={isSubmitting}
									/>
								</ListItemIcon>
								<ListItemText
									primary={getProjectCode(project) || getProjectId(project)}
									secondary={getProjectTitle(project)}
								/>
							</ListItem>
						))}
					</List>
				</>
			) : null}
			{shouldSearch && !searchResult.isFetching ? (
				<List dense className="AddScopeModalList">
					{results.map((project) => (
						<ListItem
							button
							key={getProjectId(project)}
							selected={selectedProjects.has(getProjectId(project))}
							onClick={() => {
								if (!isSubmitting) {
									handleToggleProject(project);
								}
							}}
						>
							<ListItemIcon>
								<Checkbox
									edge="start"
									checked={selectedProjects.has(getProjectId(project))}
									tabIndex={-1}
									disableRipple
									disabled={isSubmitting}
								/>
							</ListItemIcon>
							<ListItemText
								primary={getProjectCode(project) || getProjectId(project)}
								secondary={getProjectTitle(project)}
							/>
						</ListItem>
					))}
					{results.length === 0 ? (
						<ListItem>
							<ListItemText primary="No projects found" />
						</ListItem>
					) : null}
				</List>
			) : null}
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
				<Button onClick={onCancel} sx={{ mr: 1 }} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleConfirm}
					disabled={selectedProjects.size === 0 || isSubmitting}
				>
					{isSubmitting ? 'Saving...' : `Add Selected (${selectedProjects.size})`}
				</Button>
			</Box>
		</Box>
	);
};

AddProjectToUserModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	user: PropTypes.object,
	getProjectId: PropTypes.func.isRequired,
	getProjectCode: PropTypes.func.isRequired,
	getProjectTitle: PropTypes.func.isRequired,
	existingProjectIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
	isSubmitting: PropTypes.bool,
};

AddProjectToUserModal.defaultProps = {
	user: null,
	existingProjectIds: [],
	isSubmitting: false,
};

export default AddProjectToUserModal;
